/**
 * The ONE AFFDO card's adapter: a submitter's (scan level, scan method,
 * reference energy) choice → the flow the server should compile, plus the
 * parameters it binds (fs#1798, rebound to `full_affdo` by fs#2058).
 *
 * **What the card asks, in order** (`AFFDO_SCAN_METHODS_PLAN.md` rev 8 §3.13,
 * D24), the order AFFDO's own webapp used:
 * 1. the torsional scan level, `XTB` or `MM` — a card-side choice, never a
 *    manifest parameter of its own: an FFPOpt method sends it as
 *    `ffpopt_scan_level`;
 * 2. the scan method, filtered by the level, each labelled with its vendor
 *    (xtb or FFPOpt);
 * 3. the reference energy, the served manifest's `reference_level` list.
 *
 * **One flow.** The card runs `full_affdo` alone: `scan_method` (and, for an
 * FFPOpt method, `ffpopt_scan_level`) are its parameters. A server that does
 * not serve it gets no card. The old pair (`full_affdo_sequential`,
 * `full_affdo_wavefront`) is only a name past runs carry; the card's fallback
 * onto it is deleted (U6.3, fs#1796), since the bundle no longer ships it.
 *
 * **The selection invariant** ({@link settleAffdoSelection}): no selector ever
 * holds a value that is not enabled, and a move is never silent.
 *
 * **Refusals are stated, not discovered.** A combination refused at submit
 * (`qdpi2`, afs#572; `mm` + `xtb`, D22) is disabled on the card with the
 * reason its rule gives. The MACE pair on a charged ligand is different: the
 * charge reaches the tile through its `ligand` input, not a parameter, so no
 * submit-time rule can see it and the tile body refuses it only at RUNTIME
 * (exit 2), after the scan has been paid for — which is exactly the
 * "submitted to fail later" this card exists to prevent. The body stays the
 * enforcement; disabling the pair here is UX.
 *
 * Pure data and functions: no React, no fetching. The card and the submit page
 * read it; `affdo-flow-adapter.test.ts` pins every (level, method, reference)
 * combination.
 */

/** The torsional scan levels of §3.13, in display order. Card-side only. */
export const SCAN_LEVELS = ["XTB", "MM"] as const;
export type ScanLevel = (typeof SCAN_LEVELS)[number];

/** The scan methods of plan §3.1, grouped by vendor, in the manifest's order. */
export const SCAN_METHODS = ["directional", "bidirectional", "direct", "wavefront"] as const;
export type ScanMethod = (typeof SCAN_METHODS)[number];

export type ScanVendor = "xtb" | "FFPOpt";

/** Which vendor runs each method (D13): the xtb binary, or FFPOpt. */
export const SCAN_VENDORS: Record<ScanMethod, ScanVendor> = {
  directional: "xtb",
  bidirectional: "xtb",
  direct: "FFPOpt",
  wavefront: "FFPOpt",
};

/** The `ffpopt_scan_level` value an FFPOpt method sends at each level. */
const LEVEL_SCAN_MODEL: Record<ScanLevel, string> = { XTB: "xtb", MM: "mm" };

/** Each level's own default method (§3.13). */
const LEVEL_DEFAULT_METHOD: Record<ScanLevel, ScanMethod> = { XTB: "directional", MM: "direct" };

const DEFAULT_LEVEL: ScanLevel = "XTB";

export const SCAN_LEVEL_INFO: Record<ScanLevel, { label: string; description: string }> = {
  XTB: {
    label: "XTB",
    description:
      "GFN2-xTB. The xtb methods run xtb itself; the FFPOpt methods run FFPOpt with its xtb model.",
  },
  MM: {
    label: "MM",
    description: "MM (sander, the fragment's AMBER force field), via FFPOpt.",
  },
};

export const SCAN_METHOD_INFO: Record<ScanMethod, { label: string; description: string }> = {
  directional: {
    label: "Directional",
    description:
      "xtb's relaxed scan, one leg from the seed angle through 360°. AFFDO's default engine and mode (torsional_scan_engine xtb).",
  },
  bidirectional: {
    label: "Bidirectional",
    description:
      "xtb, a forward and a backward leg merged point by point, lower energy wins (AFFDO bi_directional_torsional_scan yes).",
  },
  direct: {
    label: "Direct",
    description:
      "FFPOpt's dihedral scan (ffpopt-DihedScan.py) on the ffpopt-torch image: AFFDO's seeded FFPOpt mode (ffpopt_scan_mode direct).",
  },
  wavefront: {
    label: "Wavefront",
    description:
      "FFPOpt's wavefront scan (ffpopt-DihedWavefront.py) on the ffpopt-torch image (AFFDO ffpopt_scan_mode wavefront).",
  },
};

/** The vendor tag shown beside a method at a level, so the vendor grouping
 * (D13) stays visible on every choice. */
export function vendorLabel(method: ScanMethod, level: ScanLevel): string {
  if (SCAN_VENDORS[method] === "xtb") return "via xtb";
  return level === "XTB" ? "via FFPOpt, xtb model" : "via FFPOpt";
}

/** AFFDO's seven reference levels, then FlowStack's two AIMNet2 levels (which
 * AFFDO does not have), in the order the AFFDO flows declare them. The card
 * lists what the SERVED manifest declares; this is the vocabulary it has
 * texts and refusals for. */
export const REFERENCE_LEVELS = [
  "xtb",
  "dft-sp",
  "dft",
  "qdpi2-sp",
  "qdpi2",
  "mace-sp",
  "mace",
  "aimnet2-sp",
  "aimnet2",
] as const;
export type ReferenceLevel = (typeof REFERENCE_LEVELS)[number];

function isKnownLevel(level: string): level is ReferenceLevel {
  return (REFERENCE_LEVELS as readonly string[]).includes(level);
}

/** The flow parameters the card owns. The generic parameter form must not
 * render a second control for any of them, and the request carries the
 * card's values for them, never the form's. */
export const CARD_OWNED_PARAMETERS = [
  "reference_level",
  "scan_method",
  "ffpopt_scan_level",
] as const;

const FULL_AFFDO = "full_affdo";

/** Every flow name of the AFFDO family: `full_affdo`, and the retired pair it
 * replaced, which past runs still carry (the dashboard counts them as AFFDO).
 * Only `full_affdo` folds into the card (see {@link partitionFlows}). */
export const AFFDO_FLOW_NAMES: readonly string[] = [
  "full_affdo_sequential",
  "full_affdo_wavefront",
  FULL_AFFDO,
];

/** `ffpopt_scan_level` + `reference_level` refusal (D22), in the submit-time
 * rule's own words (`affdo.constrained_opt`, afs#405). */
export const MM_XTB_REFUSAL =
  "`mm` + `xtb` is refused: for an xtb reference, run an xtb scan directly (`directional`/`bidirectional`, or FFPOpt at `xtb`). An mm scan refined at xtb is circular (GAFF torsions fitted to a GAFF/AMBER curve); refine an mm scan with dft, dft-sp, mace, mace-sp, qdpi2-sp, aimnet2 or aimnet2-sp.";

/** What both AIMNet2 levels share (AIMNet2 plan C4, C8): the model's own
 * element set, the charge rule, and the singlet limit the level's text must
 * state, because the tile checks electron parity, not the spin state. */
const AIMNET2_SCOPE =
  "Charged ligands allowed. Elements: H, B, C, N, O, F, Si, P, S, Cl, As, Se, Br, I. Runs as a singlet, so the ligand must have an even electron count; a high-spin ligand is computed as a singlet.";

/** What `aimnet2-sp`'s like-for-like DFT//XTB gate measured (afs#585,
 * R-594-1, D110). It FAILED that gate on max (2.41 / 2.17 vs 2.0 kcal/mol;
 * RMSE ≤ 0.90 passes) and is offered by operator decision (2026-10-09, afs#593).
 * The max excess is attributed to the wB97M-D3 vs PBE0-D3BJ functional
 * difference, observed on one charged ligand and not isolated from AIMNet2's
 * own model error: at identical DFT-optimised geometries AIMNet2 vs DFT gave
 * max 2.41 on tor-0 (flowstack#2031's diagnosis), which rules out geometry; a
 * wB97M-D3 DFT control, affdo-flowstack#592, would test the functional
 * attribution: "confirm" would assume its result (fs#2049). The text states all
 * of that and never words it as a pass (fs#2045). Exported for the tests only. */
export const AIMNET2_SP_CAVEAT =
  "It failed the like-for-like check against DFT//XTB (PBE0-D3BJ, one charged ligand) on maximum error: 2.41 and 2.17 kcal/mol at the highest-energy scan points, against a 2.0 kcal/mol bound; its RMSE, at most 0.90 kcal/mol, met the 1.0 kcal/mol bound. Offered by operator decision (2026-10-09). The excess is attributed to the functional difference (wB97M-D3 vs PBE0-D3BJ), but that was observed on one charged ligand and is not separated from AIMNet2's own model error: on identical DFT-optimised geometries for one torsion, AIMNet2 still differed from DFT by up to 2.41 kcal/mol, which rules out the geometry. A wB97M-D3 DFT control (affdo-flowstack#592) would test the attribution, separating the functional difference from AIMNet2's model error.";

export const REFERENCE_LEVEL_INFO: Record<ReferenceLevel, string> = {
  xtb: "The xtb scan is the reference; no refinement runs.",
  "dft-sp": "DFT single points on the scan geometries.",
  dft: "Constrained DFT optimisation at every scan point.",
  "qdpi2-sp":
    "QDπ2 single points on the scan geometries, run through FFPOpt on one GPU as AFFDO runs them. Two checks on an RTX 5090, each within the float32 bound of 2.6e-3 eV (about 0.06 kcal/mol, 6× looser than MACE's 0.01 kcal/mol): its relative energies replayed against AFFDO's recorded qdpi2-sp run, and its GPU energies against the same image on the CPU. Both measured at most 2.3e-6 eV (afs#504).",
  qdpi2: "Constrained QDπ2 optimisation at every scan point.",
  "mace-sp":
    "MACE single points on the scan geometries, run through FFPOpt as AFFDO runs them. Replayed against AFFDO's recorded mace-sp run within 0.01 kcal/mol (max difference 0.000 eV, vcm#2211). Needs a neutral ligand.",
  mace: "Constrained MACE optimisation at every scan point, run through FFPOpt. Same FFPOpt command as AFFDO; no AFFDO numeric reference exists for this level. Needs a neutral ligand.",
  "aimnet2-sp": `AIMNet2 (wB97M-D3) single points on the xtb scan geometries, run through FFPOpt on the CPU. A FlowStack level; AFFDO has no AIMNet2 level. ${AIMNET2_SCOPE} ${AIMNET2_SP_CAVEAT}`,
  aimnet2: `Constrained AIMNet2 (wB97M-D3) optimisation at every scan point, run through FFPOpt on the CPU. A FlowStack level; AFFDO has no AIMNet2 level. ${AIMNET2_SCOPE}`,
};

/** Why each refused level cannot run today. Absent = runnable. `qdpi2`'s is
 * the `affdo.constrained_opt` refusal as its manifest words it (afs#572):
 * afs#504's GPU check covers single points, so `qdpi2-sp` runs. `aimnet2-sp`
 * runs since afs#593 deleted afs#587's rule. */
const REFUSED_LEVELS: Partial<Record<ReferenceLevel, string>> = {
  qdpi2:
    "Refused for now: its constrained optimisation runs the qdpi2 model on the GPU, and the GPU numeric check that passed for it (afs#504) covers single-point energies and forces only. An optimisation needs a force-sized GPU bound, which is not defined yet. Use qdpi2-sp for a qdpi2 single-point reference, mace or mace-sp for an ML reference, dft for a DFT reference, or xtb.",
};

/** The flow input that carries the ligand on every AFFDO flow; its charge
 * decides whether the MACE pair can run. */
export const LIGAND_INPUT = "ligand";

/**
 * The selected ligand's net charge, as the catalog reports it
 * (`Artifact.molecule?.formal_charge`): a number, `null` when the stored
 * Molecule carries no readable charge, or `undefined` when there is no
 * `molecule` block (or no ligand chosen yet).
 */
export type LigandCharge = number | null | undefined;

/**
 * AFFDO's advice after a MACE refusal names its charged-ligand alternative.
 * The card never points at a level it shows disabled, so while that
 * alternative is refused here too, the advice says so and names the levels
 * that do run.
 */
function chargedLigandAdvice(alternative: ReferenceLevel): string {
  return REFUSED_LEVELS[alternative] === undefined
    ? `Please use ${alternative} instead.`
    : `AFFDO's alternative, ${alternative}, is disabled here too; use dft or xtb.`;
}

/**
 * Why a level cannot run on a ligand of this charge, or `undefined` when it
 * can. AFFDO refuses MACE on a charged ligand, and FlowStack refuses `mace-sp`
 * too rather than substitute `qdpi2-sp` (plan D5 = B). The refusal sentence
 * is AFFDO's (plan C10); the advice after it is {@link chargedLigandAdvice}'s.
 * Only a non-zero number refuses: an unknown charge (`null` or `undefined`)
 * is left to the tile body, which refuses an unreadable charge itself.
 */
function chargedLigandRefusal(level: ReferenceLevel, charge: LigandCharge): string | undefined {
  if (typeof charge !== "number" || charge === 0) return undefined;
  if (level === "mace") {
    return `MACE doesnt support charged molecules. ${chargedLigandAdvice("qdpi2")}`;
  }
  if (level === "mace-sp") {
    return `MACE-SP requires a neutral ligand (charge=0), but got charge=${charge}. ${chargedLigandAdvice("qdpi2-sp")}`;
  }
  return undefined;
}

export type Availability = { available: true } | { available: false; reason: string };

/** One value a selector offers; `reason` set = shown disabled, with why. */
export interface CardOption<T extends string> {
  value: T;
  reason?: string;
}

/**
 * Can this reference level run, whatever the scan? `offered` is the level
 * enum the served flow declares; a level missing from it would be refused by
 * the compiler, so it is refused here with that reason instead. `ligandCharge`
 * is the selected ligand's (see {@link LigandCharge}). The scan level's own
 * refusal (`MM` + `xtb`, D22) is {@link referenceLevelOptions}'.
 */
export function referenceLevelAvailability(
  level: string,
  offered?: readonly unknown[],
  ligandCharge?: LigandCharge
): Availability {
  if (isKnownLevel(level)) {
    const refused = REFUSED_LEVELS[level] ?? chargedLigandRefusal(level, ligandCharge);
    if (refused !== undefined) return { available: false, reason: refused };
  }
  if (offered !== undefined && !offered.some((v) => String(v) === level)) {
    return { available: false, reason: "The selected flow does not offer this level." };
  }
  return { available: true };
}

// ---- The served state ------------------------------------------------------

/** Does the server serve the card's flow? An old flow served without it gets
 * no card and stays a plain template. */
function servesFullAffdo(servedFlows: readonly string[]): boolean {
  return servedFlows.includes(FULL_AFFDO);
}

/** What the card's choices are evaluated against. */
export interface AffdoContext {
  /** Every flow name `/api/flows` serves (never a filtered subset). */
  servedFlows: readonly string[];
  /** The `parameters` block of `full_affdo`'s served manifest. `undefined`
   * until it has loaded: nothing it declares is decided before then. */
  parameters?: Record<string, unknown>;
  ligandCharge?: LigandCharge;
}

/** A declared enum parameter's values and default, read defensively. */
function enumParameter(
  parameters: Record<string, unknown> | undefined,
  name: string
): { values: string[]; default?: string } | undefined {
  const spec = parameters?.[name];
  if (!spec || typeof spec !== "object") return undefined;
  const { values, default: dflt } = spec as { values?: unknown; default?: unknown };
  if (!Array.isArray(values)) return undefined;
  return {
    values: values.map((v) => String(v)),
    default: dflt === undefined || dflt === null ? undefined : String(dflt),
  };
}

export function isAffdoFlow(name: string | null | undefined): boolean {
  return name !== null && name !== undefined && AFFDO_FLOW_NAMES.includes(name);
}

/** Does `name` belong to the AFFDO card on a server serving `servedFlows`?
 * Only `full_affdo` does, and only where it is served; an old flow is a plain
 * template wherever it is still served. */
export function isCardFlow(
  name: string | null | undefined,
  servedFlows: readonly string[]
): boolean {
  return name === FULL_AFFDO && servesFullAffdo(servedFlows);
}

/** Split the flows to RENDER into the AFFDO card's (rendered as one card)
 * and the rest (rendered as before, in their original order).
 *
 * `servedFlows` is the COMPLETE list `/api/flows` returned, kept apart from
 * `flows` so a filtered or searched subset can never flip the card-or-not
 * decision (Rupert PR-1860 r2): that is decided from what the server serves,
 * and `flows` only says which of them are on screen. An AFFDO flow the card
 * does not run on this server stays a plain template, reachable. */
export function partitionFlows<T extends { name: string }>(
  servedFlows: readonly string[],
  flows: readonly T[]
): { affdo: T[]; others: T[] } {
  const affdo: T[] = [];
  const others: T[] = [];
  for (const flow of flows) (isCardFlow(flow.name, servedFlows) ? affdo : others).push(flow);
  return { affdo, others };
}

/** The flow every method submits on this server (`full_affdo`, for all four),
 * or `null` when it is not served. */
export function cardFlowFor(_method: ScanMethod, servedFlows: readonly string[]): string | null {
  return servesFullAffdo(servedFlows) ? FULL_AFFDO : null;
}

/** The flow the card opens on: `full_affdo`, or `null` when the card has
 * nothing to run. */
export function cardOpeningFlow(servedFlows: readonly string[]): string | null {
  return servesFullAffdo(servedFlows) ? FULL_AFFDO : null;
}

// ---- What each selector offers ---------------------------------------------

/** The `ffpopt_scan_level` values the served flow runs at. A manifest that
 * declares no such parameter binds the tile's default, `xtb`, so an absent
 * enum means xtb only. `undefined` = not loaded yet. */
function servedScanModels(ctx: AffdoContext): string[] | undefined {
  if (ctx.parameters === undefined) return undefined;
  return enumParameter(ctx.parameters, "ffpopt_scan_level")?.values ?? ["xtb"];
}

/** The methods offered at `level`, each with why it is disabled, if it is.
 * An xtb-vendor method is never offered at `MM`. The list is the served
 * `scan_method` enum's (before it loads: the adapter's own four, all shown, so
 * nothing flickers; nothing is decided until it loads). Every method is
 * disabled on a server that does not serve `full_affdo`. */
export function scanMethodOptions(level: ScanLevel, ctx: AffdoContext): CardOption<ScanMethod>[] {
  const atLevel = (m: ScanMethod): boolean => level === "XTB" || SCAN_VENDORS[m] === "FFPOpt";
  const model = LEVEL_SCAN_MODEL[level];
  const declared = enumParameter(ctx.parameters, "scan_method")?.values;
  const offered =
    declared === undefined
      ? [...SCAN_METHODS]
      : declared.filter((v): v is ScanMethod => (SCAN_METHODS as readonly string[]).includes(v));
  const models = servedScanModels(ctx);
  return offered.filter(atLevel).map((value) => {
    if (!servesFullAffdo(ctx.servedFlows)) {
      return { value, reason: `This server does not serve ${FULL_AFFDO}.` };
    }
    if (SCAN_VENDORS[value] === "FFPOpt" && models !== undefined && !models.includes(model)) {
      return {
        value,
        reason: `This server's ${FULL_AFFDO} does not offer the ${model} FFPOpt scan model.`,
      };
    }
    return { value };
  });
}

/** Both levels, always shown (§3.13); a level with no enabled method is
 * disabled with its methods' reason. */
export function scanLevelOptions(ctx: AffdoContext): CardOption<ScanLevel>[] {
  return SCAN_LEVELS.map((value) => {
    const methods = scanMethodOptions(value, ctx);
    if (methods.some((m) => m.reason === undefined)) return { value };
    const reasons = [...new Set(methods.map((m) => m.reason))].filter(Boolean);
    return {
      value,
      reason: reasons.length > 0 ? reasons.join(" ") : "No scan method runs at this level here.",
    };
  });
}

/** The reference levels the served manifest declares, in its order, each
 * with why it is disabled at this scan level, if it is. Empty when the
 * manifest has not loaded or declares no settable `reference_level`. */
export function referenceLevelOptions(
  level: ScanLevel | undefined,
  ctx: AffdoContext
): CardOption<string>[] {
  const declared = enumParameter(ctx.parameters, "reference_level")?.values ?? [];
  return declared.map((value) => {
    const availability = referenceLevelAvailability(value, undefined, ctx.ligandCharge);
    if (!availability.available) return { value, reason: availability.reason };
    if (level === "MM" && value === "xtb") return { value, reason: MM_XTB_REFUSAL };
    return { value };
  });
}

// ---- The selection invariant -----------------------------------------------

export type AffdoSelector = "level" | "method" | "reference";

/** Why a selector's value was moved: the value the submitter last chose, and
 * the reason it is no longer enabled. */
export interface AffdoNotice {
  from: string;
  reason: string;
}

/** The card's three choices, plus the notice beside each moved selector. */
export interface AffdoSelection {
  level?: ScanLevel;
  method?: ScanMethod;
  reference?: string;
  notices: Partial<Record<AffdoSelector, AffdoNotice>>;
}

export const EMPTY_AFFDO_SELECTION: AffdoSelection = { notices: {} };

/** The display name of a selector's value, as the card labels it. */
function valueLabel(selector: AffdoSelector, value: string): string {
  if (selector === "method" && (SCAN_METHODS as readonly string[]).includes(value)) {
    return SCAN_METHOD_INFO[value as ScanMethod].label;
  }
  return value;
}

/** Keep `current` if enabled, else `preferred` if enabled, else the first
 * enabled option; `undefined` when nothing is enabled. */
function pick<T extends string>(
  options: readonly CardOption<T>[],
  current: T | undefined,
  preferred: T | undefined
): T | undefined {
  const enabled = (v: T | undefined): v is T =>
    v !== undefined && options.some((o) => o.value === v && o.reason === undefined);
  if (enabled(current)) return current;
  if (enabled(preferred)) return preferred;
  return options.find((o) => o.reason === undefined)?.value;
}

/** Why `value` is not enabled among `options`, in the words the notice uses. */
function whyNot(
  selector: AffdoSelector,
  value: string,
  options: readonly CardOption<string>[],
  level: ScanLevel | undefined
): string {
  const option = options.find((o) => o.value === value);
  if (option?.reason !== undefined) return `it is disabled: ${option.reason}`;
  if (selector === "method" && level === "MM") {
    return "it is not offered at MM: an xtb-vendor method runs only at XTB.";
  }
  return "this server's flow no longer offers it.";
}

/**
 * The selection invariant (§3.13), one rule for all three selectors.
 *
 * Evaluated level → method → reference energy, each selector keeps its value
 * when that value is enabled (offered for the current selection and not
 * disabled); otherwise it takes its own default (level `XTB`; the level's
 * default method; the served manifest's default reference level) when that
 * is enabled; else the first enabled value. A value that is not offered is
 * never a candidate, so no selector ever holds a disabled value.
 *
 * A move is never silent: whenever a value the submitter had moves, the
 * selector gains a notice naming the value they last chose (so a chained move
 * keeps the original) and why. A selector with no value yet takes its
 * default without one. Callers re-run this on every trigger — a level or
 * method change, a manifest (re)load, a new ligand — and it is idempotent.
 *
 * Nothing the served manifest declares is decided before it loads: while
 * `ctx.parameters` is `undefined`, everything is left as it is.
 */
export function settleAffdoSelection(prev: AffdoSelection, ctx: AffdoContext): AffdoSelection {
  if (!servesFullAffdo(ctx.servedFlows) || ctx.parameters === undefined) return prev;

  const notices = { ...prev.notices };
  const moved = <T extends string>(
    selector: AffdoSelector,
    before: T | undefined,
    after: T | undefined,
    options: readonly CardOption<string>[],
    level: ScanLevel | undefined
  ): void => {
    if (before === undefined || before === after) return;
    const from = notices[selector]?.from ?? valueLabel(selector, before);
    notices[selector] = { from, reason: whyNot(selector, before, options, level) };
  };

  const levels = scanLevelOptions(ctx);
  const level = pick(levels, prev.level, DEFAULT_LEVEL);
  moved("level", prev.level, level, levels, level);

  let method = prev.method;
  if (level !== undefined) {
    const methods = scanMethodOptions(level, ctx);
    method = pick(methods, prev.method, LEVEL_DEFAULT_METHOD[level]);
    moved("method", prev.method, method, methods, level);
  }

  const references = referenceLevelOptions(level, ctx);
  const declaredDefault = enumParameter(ctx.parameters, "reference_level")?.default;
  const reference = pick(references, prev.reference, declaredDefault);
  moved("reference", prev.reference, reference, references, level);

  return { level, method, reference, notices };
}

/** Two selections hold the same values and notices. */
export function sameAffdoSelection(a: AffdoSelection, b: AffdoSelection): boolean {
  return (
    a.level === b.level &&
    a.method === b.method &&
    a.reference === b.reference &&
    JSON.stringify(a.notices) === JSON.stringify(b.notices)
  );
}

/** The submitter chose `value` for `selector`: their choice replaces the
 * selector's notice, and the invariant re-settles the selectors after it. */
export function pickAffdoChoice(
  prev: AffdoSelection,
  selector: AffdoSelector,
  value: string,
  ctx: AffdoContext
): AffdoSelection {
  const { [selector]: _cleared, ...notices } = prev.notices;
  return settleAffdoSelection({ ...prev, [selector]: value, notices }, ctx);
}

// ---- What is sent ----------------------------------------------------------

export type AffdoResolution =
  | { ok: true; flowName: string; parameters: Record<string, unknown> }
  | { ok: false; reason: string };

/**
 * A card selection → the request the submit page sends.
 *
 * `full_affdo` with `scan_method`, plus `ffpopt_scan_level` from the level for
 * an FFPOpt method only — never with an xtb-vendor method. `reference_level`
 * is sent whenever the served manifest
 * declares one. The returned `parameters` are the card's whole overlay; see
 * {@link withCardParameters}. A choice the card shows disabled is refused
 * here too, with the same reason.
 */
export function resolveAffdoChoice(
  selection: Pick<AffdoSelection, "level" | "method" | "reference">,
  ctx: AffdoContext
): AffdoResolution {
  const { level, method, reference } = selection;
  if (level === undefined) return { ok: false, reason: "Pick a torsional scan level." };
  if (method === undefined) return { ok: false, reason: "Pick a scan method." };
  const methodOption = scanMethodOptions(level, ctx).find((o) => o.value === method);
  if (methodOption === undefined) {
    return { ok: false, reason: `${method} is not offered at ${level}.` };
  }
  if (methodOption.reason !== undefined) return { ok: false, reason: methodOption.reason };
  const flowName = cardFlowFor(method, ctx.servedFlows);
  if (flowName === null) return { ok: false, reason: "No AFFDO flow on this server runs it." };

  const parameters: Record<string, unknown> = { scan_method: method };
  if (SCAN_VENDORS[method] === "FFPOpt") parameters.ffpopt_scan_level = LEVEL_SCAN_MODEL[level];

  const references = referenceLevelOptions(level, ctx);
  if (references.length > 0) {
    if (reference === undefined) return { ok: false, reason: "Pick a reference energy." };
    const option = references.find((o) => o.value === reference);
    if (option === undefined) {
      return { ok: false, reason: `${reference}: The selected flow does not offer this level.` };
    }
    if (option.reason !== undefined) return { ok: false, reason: `${reference}: ${option.reason}` };
    parameters.reference_level = reference;
  }
  return { ok: true, flowName, parameters };
}

/** The request's parameters: the page's own, with every card-owned one
 * removed, then the card's overlay. Removing first is what keeps a declared
 * default (`ffpopt_scan_level: xtb`) from riding along with an xtb method. */
export function withCardParameters(
  submitted: Record<string, unknown>,
  overlay: Record<string, unknown>
): Record<string, unknown> {
  const rest = { ...submitted };
  for (const name of CARD_OWNED_PARAMETERS) delete rest[name];
  return { ...rest, ...overlay };
}

/** The manifest the generic parameter form renders: the card's parameters
 * removed, so no knob appears twice. */
export function withoutCardOwnedParameters(
  manifest: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!manifest) return manifest;
  const params = manifest.parameters;
  if (!params || typeof params !== "object") return manifest;
  const rest = { ...(params as Record<string, unknown>) };
  for (const name of CARD_OWNED_PARAMETERS) delete rest[name];
  return { ...manifest, parameters: rest };
}
